<template>
  <li @click="dumpContext" class="wt-story-preview-item">
    <transition name="wt2-fade" @afterLeave="afterLeave">
      <div v-if="!deleted" :class="{ '-deleting': deleting }">
        <div class="wt2-poster">
          <div
            v-if="versionsCount > 1 && !deleting"
            class="-badge -versions-count-badge js-stop"
            role="button"
            tabindex="0"
            :title="'storyVersions.showAllVersions' | culture"
            @click.stop="openVersions"
            @keydown.enter.stop.prevent="openVersions"
            @keydown.space.stop.prevent="openVersions"
          >
            <i class="wticon wticon-clock3 js-stop" />
            {{ versionsCount }} {{ 'storyVersions.versionsCount' | culture }}
          </div>
          <a
            v-if="!showActions && !isLockedByAI && !deleting"
            class="wt2-thumbnail-hover"
            :href="storyLink"
            :class="{ '-in-progress': isLocked }"
            :data-id="story.id"
            @click.prevent="navigateToStory"
          >
            <div v-if="story.template" class="-badge -storyboard-badge">
              {{ 'generic.storyboard' | culture }}
            </div>
            <div class="-info">
              <i v-if="story.type === 'VO'" class="wticon wticon-mic" />
            </div>
            <div class="-open-menu js-stop" @click="selectCurrentStory">
              <i class="wticon wticon-dots-three-full js-stop" />
            </div>
          </a>
          <story-preview-card-actions
            v-if="isSelected && showActions && !showConfirm && !deleting"
            :data-id="story.id"
            :isLocked="isLocked"
            @action="doAction"
          />
          <wt-thumbnail-confirm
            v-show="showConfirm"
            :message="deleteConfirmMessage"
            @no="showConfirm = showActions = false"
            @yes="deleteStory"
          />
          <div class="-inner">
            <img
              v-if="isLockedByAI"
              src="/resources/img/lockedAiThumbnail.jpg"
            />
            <img v-else-if="poster" :src="poster" />
            <div v-else class="-no-thumb" />

            <div v-if="deleting" class="wt2-story-locked -deleting-overlay">
              <i class="wticon wticon-cog" />
              <div class="wt2-text-n -text">
                {{ 'storyPreview.deleting' | culture }}
              </div>
            </div>

            <template v-if="!showActions && !deleting">
              <story-preview-status-message
                v-if="failed"
                icon="wticon-close-s"
                class="wt2-bgcolor-error"
                :title="'storyPreview.failed' | culture"
              >
                <div class="wt2-text-m">
                  {{ 'storyPreview.failed' | culture }}
                </div>
                <p v-text="failedText" />
              </story-preview-status-message>

              <a
                v-if="isLocked || isProducing"
                class="wt2-story-locked"
                :href="storyLink"
                :data-id="story.id"
                @click.prevent
              >
                <template v-if="isProducing">
                  <i class="wticon wticon-cog" />
                  <div class="wt2-text-n -text">
                    {{ 'storyPreview.producing' | culture }}
                  </div>
                </template>
                <template v-else>
                  <i class="wticon wticon-lock2" />
                  <div class="wt2-text-n -text">
                    <template v-if="isLockedByAI">
                      AI story creation in progress
                    </template>
                    <template v-else>
                      {{ 'storyPreview.inUse' | culture }}
                      {{ story.lockedUserName }}
                    </template>
                  </div>
                </template>
                <div class="-info">
                  <i v-if="story.type === 'VO'" class="wticon wticon-mic" />
                </div>
              </a>
            </template>
          </div>
        </div>

        <footer class="wt-preview-item-footer">
          <div class="-top-line">
            <div
              class="wt2-timestamp wt2-text-s"
              :class="{ '-divider': hasMoreThenOneProgrammer }"
              :title="lastUpdateFull"
            >
              {{ lastUpdate }}
              <span v-if="isDev" class="-dev-info"
                >({{ taskType }}|{{ story.state }})</span
              >
            </div>
            <div
              v-if="hasMoreThenOneProgrammer"
              class="-programmer-name wt2-text-s"
              :title="story.programmerName"
              v-text="story.programmerName"
            />
          </div>
          <div class="-bottom-line">
            <div
              class="wt2-title"
              :title="story.title"
              v-text="story.title || 'Untitled'"
            />
          </div>
        </footer>
      </div>
    </transition>
  </li>
</template>

<script>
import app from 'app';
import Analytics from 'c/WtAnalytics';
import { DuplicateController } from 'c/DuplicateController';
import { mapGetters, mapMutations } from 'vuex';
import StoryPreviewStatusMessage from './StoryPreviewStatusMessage.vue';
import StoryPreviewCardActions from './StoryPreviewCardActions.vue';
import { isAiLock } from '#/const';

export default {
  name: 'story-preview-item',
  components: {
    StoryPreviewStatusMessage,
    StoryPreviewCardActions,
  },

  props: {
    story: {
      type: Object,
      required: true,
    },
    tab: {
      type: String,
    },
    // Set only for grouped/family cards. Default 0 keeps ungrouped rendering
    // byte-identical to before (no badge, editor-tap behavior unchanged).
    versionsCount: {
      type: Number,
      default: 0,
    },
    // A folder delete is in progress for this card: lock it (block all taps)
    // and show the deleting overlay instead of a full-screen spinner.
    deleting: {
      type: Boolean,
      default: false,
    },
  },

  data: function () {
    return {
      failedText: app.culture.story.Failed_Producing,
      isDev: false /* app.rootStore.getters['user/box'].isDev()*/,
      showActions: false,
      showConfirm: false,
      deleted: false,
      hasMoreThenOneProgrammer: this.$store.getters.hasMoreThenOneProgrammer,
    };
  },

  watch: {
    showActions() {
      this.$_lockAutorefresh();
    },

    showConfirm() {
      this.$_lockAutorefresh();
    },

    isSelected(newVal) {
      if (!newVal) {
        this.showActions = false;
        this.showConfirm = false;
      }
    },
  },

  computed: {
    ...mapGetters(['selectedStory']),

    isLockedByAI() {
      return isAiLock({
        userId: this.story?.lockedUserId,
        userName: this.story?.lockedUserName,
      });
    },

    taskType() {
      const type = this.story.taskType;
      if (type === 'ASSET_MODERATION') {
        return 'MS';
      }
      if (type === 'NARRATION') {
        return 'VO';
      }
      if (type === 'VIDEO_MODERATION') {
        return 'TL';
      }

      return 'N/A';
    },

    storyLink() {
      return this.isProducing
        ? `/videos/producing/${this.story.id}`
        : `/stories/${this.story.id}`;
    },

    poster() {
      return app.prependHttp(this.story.thumbnailPath);
    },

    failed() {
      return !!this.story.failed;
    },

    isProducing() {
      return !!this.story.inProducing;
    },

    lastUpdate() {
      const millisecondsFromNow =
        this.$store.state.currentTime - this.story.lastUpdate;
      return app.TIME.getTimeAgo(millisecondsFromNow);
    },

    lastUpdateFull() {
      return app.moment(this.story.lastUpdate).format('MM/DD/YYYY hh:mma');
    },

    isSelected() {
      return this.selectedStory === this.story.id;
    },

    isLocked() {
      return !!this.story.locked;
    },

    deleteConfirmMessage() {
      return this.versionsCount > 1
        ? app.culture.storyVersions.confirmDeleteVersion
        : app.culture.confirmDelete.message;
    },
  },

  methods: {
    ...mapMutations(['setSelectedStory']),

    $_lockAutorefresh() {
      this.$store.state.dashboard.lock.autorefresh =
        this.showActions || this.showConfirm;
    },

    /**
     * @param e {Event}
     */
    dumpContext(e) {
      if (e.metaKey || e.ctrlKey) {
        this.$log('Story preview', this.story);
      }
    },

    /**
     * @param [e] {Event}
     */
    navigateToStory(e) {
      if (e && (e.metaKey || e.ctrlKey)) {
        return;
      }

      if (this.isLocked || (e && e.target.classList.contains('js-stop'))) {
        return;
      }

      const options = {
        source: e ? 'Clicked on thumbnail' : 'Clicked on edit button',
        tab: this.tab,
      };

      Analytics.trigger('analytics:dashboard:openStory', options);
      app.navigate(this.storyLink);
    },

    doAction: function ({ type }) {
      if ('close' === type) {
        this.showActions = false;
      } else if ('edit' === type) {
        this.navigateToStory();
      } else if ('delete' === type) {
        // Family card: defer to the parent group's "delete all versions" modal.
        // Standalone draft: keep the existing inline confirmation.
        if (this.versionsCount > 1) {
          this.showActions = false;
          this.$emit('request-folder-delete');
        } else {
          this.showConfirm = true;
        }
      } else if ('duplicate' === type) {
        DuplicateController.openDialog({
          story: this.story,
          source: 'dashboard',
          message: app.culture.makeCopyDialog.messageStory,
          storyStartPoint: app.storyStartPoint.DUPLICATE_IN_PROGRESS_STORY,
        }).then((data) => {
          if (data.changeOrg) {
            app.delayedSpinner.hide();
          } else {
            app.navigate(`stories/${data.storyId}`);
          }
        });
      }
    },

    selectCurrentStory() {
      this.showActions = true;
      this.setSelectedStory(this.story.id);
    },

    openVersions() {
      Analytics.trigger('analytics:versions:pageOpened', {
        context: 'dashboard',
        versionsCount: this.versionsCount,
      });
      app.navigate(`dashboard/versions/${this.story.id}`);
    },

    afterLeave() {
      if (!this.deleted) {
        // shouldn't happen, just a safety measure
        return;
      }
      const id = this.story.id;
      const story = this.story;

      Analytics.trigger('analytics:dashboard:deleteStory', { tab: this.tab });

      // Remove the card immediately so the grid reflows without waiting for the
      // network round-trip (a failed delete is rolled back in .fail below).
      this.$store.commit('deleteStory', id);

      app.api.draftStories
        .delete({ id, template: story.template })
        .done(() => {
          this.$emit('itemRemoved');
          // Grouped cards let their wrapper refetch the (reshaped) version group.
          this.$emit('deleted-in-group', id);
        })
        .fail((err) => {
          // Nothing was deleted server-side (e.g. a 423 on a lone root of a
          // locked family) - restore the row.
          this.$store.commit('upsertStoryPreviewFirst', story);
          if (err && err.errorCode === 423) {
            app.dialog.error({
              type: app.dialog.types.ERROR_1,
              title: app.culture.storyVersions.deleteLocked,
            });
          } else {
            console.error(`failed deleting story ${id}`);
          }
        });
    },

    deleteStory() {
      this.deleted = true;
    },
  },
};
</script>
